import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
  HiOutlineArrowLeft,
  HiOutlinePlus,
  HiOutlineMinus,
  HiOutlineCalendar,
  HiOutlineCash,
  HiOutlineDocumentText,
  HiOutlineUser
} from 'react-icons/hi';
import { liabilityAPI } from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/helpers';
import {
  PageHeader,
  Card,
  Button,
  Input,
  Select,
  Textarea,
  Badge,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableEmpty
} from '../../components/common';
import Modal from '../../components/common/Modal';
import { PageLoader } from '../../components/common/Spinner';

const Liability = () => {
  const [summary, setSummary] = useState(null);
  const [liabilities, setLiabilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('borrow');
  const [selectedLender, setSelectedLender] = useState(null);

  const [formData, setFormData] = useState({
    lenderName: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [summaryRes, liabilitiesRes] = await Promise.all([
        liabilityAPI.getSummary(),
        liabilityAPI.getAll({ limit: 100 })
      ]);
      if (summaryRes.success) setSummary(summaryRes.data);
      if (liabilitiesRes.success) setLiabilities(liabilitiesRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (type, lender = null) => {
    setModalType(type);
    setSelectedLender(lender);
    setFormData({
      lenderName: lender?.lenderName || '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setErrors({});
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedLender(null);
    setFormData({ lenderName: '', amount: '', date: '', notes: '' });
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.lenderName?.trim()) {
      newErrors.lenderName = 'Please enter the lender name';
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    if (!formData.date) {
      newErrors.date = 'Please select a date';
    }
    if (modalType === 'return') {
      const lender = summary?.lenders?.find(
        l => l.lenderName.toLowerCase() === formData.lenderName.trim().toLowerCase()
      );
      const outstanding = lender?.balance || 0;
      if (parseFloat(formData.amount) > outstanding) {
        newErrors.amount = `Amount cannot exceed outstanding balance (${formatCurrency(outstanding)})`;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      await liabilityAPI.create({
        lenderName: formData.lenderName.trim(),
        amount: parseFloat(formData.amount),
        type: modalType === 'borrow' ? 'Borrowed' : 'Returned',
        date: formData.date,
        notes: formData.notes?.trim() || null
      });

      toast.success(`Loan ${modalType === 'borrow' ? 'borrowed' : 'returned'} successfully`);
      fetchData();
      closeModal();
    } catch (error) {
      toast.error(error.message || 'Failed to record liability');
    } finally {
      setSubmitting(false);
    }
  };

  const getLenderBalance = (lenderName) => {
    const lender = summary?.lenders?.find(
      l => l.lenderName.toLowerCase() === (lenderName || '').toLowerCase()
    );
    return lender?.balance ?? 0;
  };

  if (loading) return <PageLoader />;

  const lenders = summary?.lenders || [];
  const totalBorrowed = summary?.totalBorrowed ?? 0;
  const totalReturned = summary?.totalReturned ?? 0;
  const outstanding = summary?.outstanding ?? 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Liabilities"
        subtitle="Manage loans borrowed from others and repayments"
        breadcrumbs={[{ label: 'Liabilities' }]}
        action={
          <Button
            variant="ghost"
            icon={HiOutlineArrowLeft}
            onClick={() => window.history.back()}
          >
            Back
          </Button>
        }
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600">
          <div className="text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Borrowed</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(totalBorrowed)}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <HiOutlinePlus className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600">
          <div className="text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Total Returned</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(totalReturned)}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <HiOutlineMinus className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600">
          <div className="text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Outstanding</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(outstanding)}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <HiOutlineCash className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600">
          <div className="text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Lenders</p>
                <p className="text-2xl font-bold mt-1">{lenders.length}</p>
              </div>
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <HiOutlineDocumentText className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lenders with Balances */}
        <Card className="lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Lender Balances</h3>
            <div className="flex gap-2">
              <Button size="sm" icon={HiOutlinePlus} onClick={() => openModal('borrow')}>
                Borrow
              </Button>
              <Button
                size="sm"
                variant="outline"
                icon={HiOutlineMinus}
                onClick={() => openModal('return')}
                disabled={lenders.length === 0}
              >
                Return
              </Button>
            </div>
          </div>

          <div className="space-y-3 max-h-[600px] overflow-y-auto">
            {lenders.length === 0 ? (
              <p className="text-center py-8 text-gray-500">No loans recorded yet</p>
            ) : (
              lenders.map((lender) => (
                <div
                  key={lender.lenderName}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedLender?.lenderName === lender.lenderName
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                        <HiOutlineUser className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{lender.lenderName}</p>
                        <Badge variant="warning" className="text-xs mt-1">Outstanding</Badge>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-orange-600">{formatCurrency(lender.balance)}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="flex-1 text-xs"
                      icon={HiOutlinePlus}
                      onClick={() => openModal('borrow', lender)}
                    >
                      Borrow More
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-xs"
                      icon={HiOutlineMinus}
                      onClick={() => openModal('return', lender)}
                    >
                      Return
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Transaction History */}
        <Card className="lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">All Transactions</h3>
            <Badge variant="default">{liabilities.length} transactions</Badge>
          </div>

          <Table>
            <TableHead>
              <TableHeader>Date</TableHeader>
              <TableHeader>Lender</TableHeader>
              <TableHeader>Type</TableHeader>
              <TableHeader>Amount</TableHeader>
              <TableHeader>Notes</TableHeader>
            </TableHead>
            <TableBody>
              {liabilities.length === 0 ? (
                <TableEmpty message="No transactions yet" colSpan={5} />
              ) : (
                liabilities.map((t) => (
                  <TableRow key={t._id || t.id}>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <HiOutlineCalendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{formatDate(t.date)}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                          <HiOutlineUser className="w-4 h-4 text-gray-600" />
                        </div>
                        <span className="font-medium text-gray-900">{t.lenderName}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={t.type === 'Borrowed' ? 'info' : 'success'}>
                        {t.type === 'Borrowed' ? (
                          <span className="flex items-center gap-1">
                            <HiOutlinePlus className="w-3 h-3" /> Borrowed
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <HiOutlineMinus className="w-3 h-3" /> Returned
                          </span>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`font-semibold ${
                          t.type === 'Borrowed' ? 'text-blue-600' : 'text-emerald-600'
                        }`}
                      >
                        {t.type === 'Borrowed' ? '+' : '-'}
                        {formatCurrency(t.amount)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-500">{t.notes || '-'}</span>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Borrow / Return Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={modalType === 'borrow' ? 'Borrow Loan' : 'Return Loan'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {modalType === 'return' && lenders.length > 0 ? (
            <Select
              label="Select Lender"
              name="lenderName"
              value={formData.lenderName}
              onChange={handleChange}
              options={lenders.map(l => ({
                value: l.lenderName,
                label: `${l.lenderName} (Balance: ${formatCurrency(l.balance)})`
              }))}
              placeholder="Choose a lender"
              error={errors.lenderName}
              required
            />
          ) : (
            <Input
              label="Lender Name"
              name="lenderName"
              value={formData.lenderName}
              onChange={handleChange}
              placeholder="Enter name of person you're borrowing from"
              error={errors.lenderName}
              required
            />
          )}

          <Input
            label="Amount"
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            placeholder="Enter amount"
            prefix="Rs."
            error={errors.amount}
            required
          />

          <Input
            label="Date"
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            error={errors.date}
            required
          />

          <Textarea
            label="Notes (Optional)"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Add any notes..."
            rows={3}
          />

          {formData.lenderName && modalType === 'return' && (
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-600">
                Outstanding:{' '}
                <span className="font-semibold text-gray-900">
                  {formatCurrency(getLenderBalance(formData.lenderName))}
                </span>
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={submitting}
              icon={modalType === 'borrow' ? HiOutlinePlus : HiOutlineMinus}
            >
              {modalType === 'borrow' ? 'Borrow' : 'Return'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Liability;
